import { Metadata, Schema, SchemaField } from '../../types';
import { ACState } from './accessControlReducer';
import { NodeType } from '../../enums/NodeType';
import { FieldType } from '../../enums/FieldType';
import { Props } from './accessControl';
import { useSelector } from '../../utils/useSelector';
import { JobRole } from '../../enums/JobRole';
import { Role } from '../../enums/Role';
import { getSchemaFieldPermission } from './permissions/getSchemaFieldPermission';
import { getSchemaPermission } from './permissions/getSchemaPermission';
import { getResourcePermission } from './permissions/getResourcePermission';
import { GetPermissionPayload } from './permissions/types';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { ItemType } from '../../enums/ItemType';
import { getItemType } from '../../utils';

interface Permission {
  canView: boolean;
  canEdit: boolean;
}

type P = Pick<Props, 'resource' | 'data' | 'selectedWorkspace' | 'isEditMode'>;

export const getPermission = ({
  resource,
  data,
  selectedWorkspace
}: P): Permission => {
  const roles = useSelector(
    s =>
      getAllRoles(s.accessControl, s.planning?.selectedWorkspace?.props?.refId),
    (a, b) => deepEquals(a, b)
  );
  const user = useSelector(s => s.app.user);
  const jobRoles = user.jobRoles;
  const workspace = useSelector(s => s.planning?.selectedWorkspace);
  const isEditMode = useSelector(s => s.view?.editMode);

  const payload: GetPermissionPayload = {
    workspace,
    roles,
    jobRoles,
    data,
    isEditMode,
    isWorkspaceOwner: hasRole(roles, Role.admin, Role.developer, Role.owner),
    isWorkspaceDataManager: hasRole(roles, Role.dataManager),
    isItemTypePlanner: isItemTypePlanner(roles, data),
    empCd: user?.employeeId,
    hasJobRole: (...list: JobRole[]) => hasRole(jobRoles, ...list),
    hasRole: (...list: Role[]) => hasRole(roles, ...list),
    isPracticeAdmin: () => {
      return [
        'timothy.weaver@gartner.com',
        'cecile.drew@gartner.com',
        'andrew.white@gartner.com'
      ].includes(user?.email?.toLocaleLowerCase());
    }
  };
  if (!roles) return null;
  if (isSchemaField(resource)) {
    return getSchemaFieldPermission({
      ...payload,
      schemaField: resource,
      isEditMode
    });
  } else if (isSchema(resource)) {
    return getSchemaPermission({
      ...payload,
      schema: resource,
      selectedWorkspace
    });
  } else {
    return getResourcePermission({
      ...payload,
      resource
    });
  }
};

export const getAllRoles = (state: ACState, workspaceRefId: string) => {
  const { roles, permissions } = state || {};
  if (!roles) return null;
  const wRoles =
    permissions &&
    permissions[workspaceRefId] &&
    permissions[workspaceRefId].roles;
  return [...(roles || []), ...(wRoles || [])] as Role[];
};

export const isOwner = (roles: Role[]) =>
  hasRole(roles, Role.admin, Role.developer, Role.owner);

export const isPlanner = (roles: Role[]) => hasRole(roles, Role.planner);

export const isDataManager = (roles: Role[]) =>
  hasRole(roles, Role.dataManager);

export const isItemTypePlanner = (roles: Role[], item: Partial<Metadata>) => {
  const itemType = getItemType(item);
  return (
    (itemType == ItemType.content && hasRole(roles, Role.planner)) ||
    (itemType == ItemType.globalSubmission &&
      hasRole(roles, Role.conferencePlanner))
  );
};

const hasRole = <T>(roles: T[], ...list: T[]) => {
  if (roles) {
    for (const role of list) {
      if (roles.includes(role)) return true;
    }
  }
  return false;
};

const isSchemaField = (s: any): s is SchemaField =>
  s?.field &&
  Object.values(FieldType).includes(s.fieldType) &&
  s.dataType &&
  s.key;

const isSchema = (s: any): s is Schema =>
  Array.isArray(s?.labels) &&
  s.labels.includes(NodeType.Schema) &&
  s.props?.type &&
  Array.isArray(s.props.fields);
